import {
  type AxiosInstance,
  type AxiosResponse,
  HttpStatusCode,
  isAxiosError,
} from "axios";
import fileDownload from "js-file-download";
import { getAttachedFileNameFromHeader } from "@/utils/utils";
import { SendToTrackingService } from "@/api/handlers/errors";
import { CORPORATE_MASTER_LIST_LIMIT } from "../constants";
import type {
  CorporateListParams,
  CorporateListResponse,
  CorporateMasterDetail,
  CorporateReceptionBooksParams,
  CorporateSuggestResponse,
  RegistryTagOptionResponse,
  ICommercialFeedResponse,
  KanpoNoticeListResponse,
} from "../types";

/** 法人マスタ 一覧検索APIのパス */
export const CORPORATE_MASTER_LIST_API_PATH = "/corporate-master/";
/** 法人マスタ 法人名サジェストAPIのパス */
export const CORPORATE_MASTER_SUGGEST_API_PATH = "/corporate-master/suggest";
/** 法人マスタ タグ（公表情報）一覧APIのパス */
export const CORPORATE_MASTER_REGISTRY_TAGS_API_PATH =
  "/corporate-master/registry-tags";
/** 法人マスタ 選んだ法人の Excel 出力APIのパス */
export const CORPORATE_MASTER_FILE_API_PATH = "/corporate-master/file";

/**
 * 法人マスタ 詳細（集約）APIのパス
 * @param corporateNumber 法人番号（13桁）
 * @returns APIパス
 */
export const corporateMasterDetailApiPath = (corporateNumber: string): string =>
  `/corporate-master/${corporateNumber}`;

/**
 * 法人マスタ 商業登記受付帳の一覧APIのパス
 * @param corporateNumber 法人番号（13桁）
 * @returns APIパス
 */
export const corporateReceptionBooksApiPath = (
  corporateNumber: string
): string => `/corporate-master/${corporateNumber}/reception-books`;

/**
 * 一覧検索のクエリから未指定（undefined・空文字・空配列）を除き、limit/offset を補う
 * @param params 検索条件
 * @returns APIへ送るクエリ
 */
export const buildCorporateListQuery = (
  params: CorporateListParams
): CorporateListParams => {
  const query: CorporateListParams = {};
  (Object.keys(params) as Array<keyof CorporateListParams>).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === "") return;
    if (Array.isArray(value) && value.length === 0) return;
    Object.assign(query, { [key]: value });
  });
  query.limit = Math.min(
    query.limit ?? CORPORATE_MASTER_LIST_LIMIT,
    CORPORATE_MASTER_LIST_LIMIT
  );
  query.offset ??= 0;
  return query;
};

/**
 * 法人マスタを検索する
 * @param apiClient APIクライアント
 * @param query 検索クエリ（buildCorporateListQuery 済み）
 * @returns 検索結果（件数と一覧）
 */
export const getCorporateMasterList = async (
  apiClient: AxiosInstance,
  query: CorporateListParams
): Promise<CorporateListResponse> => {
  const { data } = await apiClient.get<CorporateListResponse>(
    CORPORATE_MASTER_LIST_API_PATH,
    // Django側の getlist（prefecture/city/kind/commercial_status）に合わせ、配列は同名キーの繰り返しにする。
    { params: query, paramsSerializer: { indexes: null } }
  );
  return data;
};

/**
 * 法人マスタの詳細（集約）を取得する。未登録の法人番号（404）は null を返す
 * @param apiClient APIクライアント
 * @param corporateNumber 法人番号（13桁）
 * @returns 詳細。見つからない場合は null
 */
export const getCorporateMasterDetail = async (
  apiClient: AxiosInstance,
  corporateNumber: string
): Promise<CorporateMasterDetail | null> => {
  try {
    const { data } = await apiClient.get<CorporateMasterDetail>(
      corporateMasterDetailApiPath(corporateNumber)
    );
    // 公表情報のタグを返さない BE（タグの PR より前）でも詳細を表示できるようにする
    return { ...data, registryTags: data.registryTags ?? [] };
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
};

/**
 * 法人名のサジェスト候補を取得する
 * @param apiClient APIクライアント
 * @param q 入力文字列
 * @param limit 最大件数
 * @returns 候補一覧
 */
export const getCorporateMasterSuggest = async (
  apiClient: AxiosInstance,
  q: string,
  limit: number
): Promise<CorporateSuggestResponse> => {
  const { data } = await apiClient.get<CorporateSuggestResponse>(
    CORPORATE_MASTER_SUGGEST_API_PATH,
    { params: { q, limit } }
  );
  return data;
};

/**
 * 公的名簿から決まるタグ（公表情報）の一覧を取得する。検索条件の選択肢に使う
 * @param apiClient APIクライアント
 * @returns タグの一覧
 */
export const getCorporateMasterRegistryTags = async (
  apiClient: AxiosInstance
): Promise<RegistryTagOptionResponse> => {
  const { data } = await apiClient.get<RegistryTagOptionResponse>(
    CORPORATE_MASTER_REGISTRY_TAGS_API_PATH
  );
  return data;
};

/**
 * 法人に紐付いた商業登記受付帳の履歴を1ページ分取得する
 * @param apiClient APIクライアント
 * @param corporateNumber 法人番号（13桁）
 * @param params ページ指定
 * @returns 受付帳の履歴（count は全件数、list は当該ページ分）
 */
export const getCorporateReceptionBooks = async (
  apiClient: AxiosInstance,
  corporateNumber: string,
  params: CorporateReceptionBooksParams
): Promise<ICommercialFeedResponse> => {
  const { data } = await apiClient.get<ICommercialFeedResponse>(
    corporateReceptionBooksApiPath(corporateNumber),
    { params }
  );
  return data;
};

/**
 * 取得済み商業登記PDFのダウンロードURLを、押下時にBEで発行する（署名付きURLの失効対策）
 * @param apiClient APIクライアント
 * @param corporateNumber 13桁法人番号
 * @param analyzedCorporationId 商業登記サマリの解析依頼明細ID
 * @returns ダウンロードURL。PDFの保存先が無ければ null
 */
export const getCommercialAnalysisDownloadUrlAPI = async (
  apiClient: AxiosInstance,
  corporateNumber: string,
  analyzedCorporationId: number
): Promise<string | null> => {
  const { data } = await apiClient.get<{ downloadUrl: string | null }>(
    `${CORPORATE_MASTER_LIST_API_PATH}${corporateNumber}/commercial-analysis/${analyzedCorporationId}/download`
  );
  return data.downloadUrl;
};

/**
 * 法人マスタに紐づいた官報の公告一覧APIのパス
 * @param corporateNumber 法人番号（13桁）
 * @returns APIパス
 */
export const corporateMasterKanpoNoticesApiPath = (
  corporateNumber: string
): string => `/corporate-master/${corporateNumber}/kanpo-notices`;

/**
 * 法人マスタに紐づいた官報の公告を、公告日の新しい順に取得する
 * @param apiClient APIクライアント
 * @param corporateNumber 法人番号（13桁）
 * @returns 公告の件数と一覧
 */
export const getCorporateKanpoNotices = async (
  apiClient: AxiosInstance,
  corporateNumber: string
): Promise<KanpoNoticeListResponse> => {
  const { data } = await apiClient.get<KanpoNoticeListResponse>(
    corporateMasterKanpoNoticesApiPath(corporateNumber)
  );
  return data;
};

/**
 * 官報の号のPDFの署名付きURLを、押下時にBEで発行する（署名付きURLの失効対策）
 * @param apiClient APIクライアント
 * @param corporateNumber 法人番号（13桁）
 * @param noticeLinkId 公告一覧の id（公告と法人の紐づきのID）
 * @returns 署名付きURL。`#page=ページ番号` を付けて開く
 */
export const getKanpoNoticePdfUrl = async (
  apiClient: AxiosInstance,
  corporateNumber: string,
  noticeLinkId: number
): Promise<string> => {
  const { data } = await apiClient.get<{ downloadUrl: string }>(
    `${corporateMasterKanpoNoticesApiPath(
      corporateNumber
    )}/${noticeLinkId}/pdf-url`
  );
  return data.downloadUrl;
};

/**
 * 選んだ法人を Excel に出力し、ダウンロードを始める。行は渡した順に並ぶ
 * @param apiClient APIクライアント
 * @param corporateNumbers 法人番号（13桁）の一覧（一覧の並び順）
 */
export const downloadCorporateMasterExcel = async (
  apiClient: AxiosInstance,
  corporateNumbers: readonly string[]
): Promise<void> => {
  try {
    const response = await apiClient.post<Blob>(
      CORPORATE_MASTER_FILE_API_PATH,
      { corporateNumbers },
      { responseType: "blob" }
    );
    fileDownload(
      response.data,
      getAttachedFileNameFromHeader(response, "法人マスタ.xlsx")
    );
  } catch (error) {
    if (
      isAxiosError<AxiosResponse>(error) &&
      (error.response?.status ?? HttpStatusCode.InternalServerError) >=
        HttpStatusCode.InternalServerError
    ) {
      SendToTrackingService(error);
    }
    throw error;
  }
};
